import { useTranslation } from 'react-i18next';
import type { ComplianceQueueItem } from '@shared/api/contracts/compliance';
import { formatRelativeHold, holdReasonKey, RiskBandBadge } from '@entities/compliance';
import { WithdrawalStatusBadge } from '@entities/withdrawal';
import type { WithdrawalStatus } from '@shared/api/contracts';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import {
  CopyableHash,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  TypographyMuted,
} from '@shared/ui';

const FILTERS = ['', 'aml_pending', 'manual_review', 'info_requested'] as const;

interface ComplianceQueueProps {
  items: ComplianceQueueItem[];
  page: number;
  limit: number;
  total: number;
  status: string;
  onStatus: (status: string) => void;
  onPage: (page: number) => void;
  onOpen: (id: number) => void;
}

export function ComplianceQueue({ items, page, limit, total, status, onStatus, onPage, onOpen }: ComplianceQueueProps) {
  const { t } = useTranslation('cabinet');
  const { copy } = useCopyToClipboard();

  return (
    <div className="surface-card min-w-0 space-y-4">
      <Tabs value={status} onValueChange={onStatus}>
        <TabsList className="h-auto w-full flex-wrap">
          {FILTERS.map((filter) => (
            <TabsTrigger key={filter || 'all'} value={filter} className="flex-none px-3">
              {t(filter ? `withdrawals.status.${filter}` : 'compliance.filter.all')}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {!items.length ? (
        <TypographyMuted className="block py-10 text-center">{t('compliance.queue.empty')}</TypographyMuted>
      ) : (
        <div className="min-w-0 overflow-x-auto">
          <Table className="min-w-[64rem]">
            <TableHeader>
              <TableRow>
                <TableHead>{t('withdrawals.columns.id')}</TableHead>
                <TableHead>{t('compliance.columns.merchant')}</TableHead>
                <TableHead>{t('withdrawals.columns.amount')}</TableHead>
                <TableHead>{t('withdrawals.columns.address')}</TableHead>
                <TableHead>{t('withdrawals.columns.status')}</TableHead>
                <TableHead>{t('compliance.columns.reason')}</TableHead>
                <TableHead>{t('compliance.columns.since')}</TableHead>
                <TableHead>{t('compliance.columns.risk')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const relative = formatRelativeHold(item.holdSince);
                return (
                  <TableRow key={item.id} className="cursor-pointer" onClick={() => onOpen(item.id)}>
                    <TableCell className="font-medium">
                      {item.id}
                      {item.pendingDecision ? (
                        <span className="mt-1 block text-xs text-muted-foreground">{t('compliance.waitingSecond')}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="max-w-[12rem] truncate">{item.merchant.email}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {formatLedgerAmount(item.amount, { assetLabel: item.assetCode })}
                      <span className="ml-1 text-xs text-muted-foreground">{item.network}</span>
                    </TableCell>
                    <TableCell className="max-w-[10rem]" onClick={(event) => event.stopPropagation()}>
                      <CopyableHash value={item.address} onCopy={() => copy(item.address, undefined, item.address)} />
                    </TableCell>
                    <TableCell>
                      <WithdrawalStatusBadge status={item.status as WithdrawalStatus} audience="admin" />
                    </TableCell>
                    <TableCell className="max-w-[14rem] text-sm">{t(holdReasonKey(item.holdReason))}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {t(`compliance.relative.${relative.unit}`, { count: relative.count })}
                    </TableCell>
                    <TableCell>
                      <RiskBandBadge
                        band={item.latestCheck?.band}
                        score={item.latestCheck?.riskscore}
                        scoreBand={item.latestCheck?.scoreBand}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      <Pagination page={page} limit={limit} total={total} onPageChange={onPage} />
    </div>
  );
}
